import { Box } from "@mui/material";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import type {
  ManualChapterText,
  ManualTopicText,
} from "./ManualChapterSection";
import { chapterNumber, type ManualChapterId } from "./manualChapters";
import { MANUAL_STYLES } from "./manualStyles";
import { MANUAL_CHAPTERS } from "./manualTopics";

export interface ManualChapterListProps {
  /** 펼침 단추의 `aria-controls` 대상 */
  id: string;
  hidden: boolean;
  /** `side` = 넓은 화면 왼쪽 열 맨 아래(장만), `bar` = 좁은 화면 아래 막대 위로 펼치는 목록(지금 장의 절까지) */
  variant: "side" | "bar";
  chapters: Readonly<Record<ManualChapterId, ManualChapterText>>;
  currentChapter: ManualChapterId | null;
  /** 지금 장 아래에 절 목록을 펼친다(`bar`) — 지금 절을 함께 표시한다 */
  topics?: {
    texts: Readonly<Record<string, ManualTopicText>>;
    current: string | null;
  };
  /** 고른 장·절로 옮긴다(바로 이동 + 목록 닫기는 사용처가) */
  onJump: (id: string) => void;
}

/** 전체 목차(service-manual 「매뉴얼 장 구성」): 여덟 장, 지금 장 표시 */
export function ManualChapterList({
  id,
  hidden,
  variant,
  chapters,
  currentChapter,
  topics,
  onJump,
}: ManualChapterListProps) {
  const { t } = useTranslation("landing");
  const jump = (target: string) => (event: MouseEvent) => {
    event.preventDefault();
    onJump(target);
  };
  return (
    <Box
      component="ol"
      id={id}
      hidden={hidden}
      aria-label={t("site.manual.allChapters")}
      sx={
        variant === "side"
          ? MANUAL_STYLES.sideChapterList
          : MANUAL_STYLES.barChapterList
      }
    >
      {MANUAL_CHAPTERS.map((chapter, index) => {
        const isCurrent = chapter.id === currentChapter;
        const name = chapters[chapter.id].name;
        return (
          <li key={chapter.id}>
            <a
              href={`#${chapter.id}`}
              aria-current={isCurrent ? "true" : undefined}
              onClick={jump(chapter.id)}
            >
              <span data-n="">{chapterNumber(index)}</span>
              {name}
            </a>
            {isCurrent && topics && (
              <ol aria-label={t("site.manual.tocLabel", { chapter: name })}>
                {chapter.topics.map(({ id: topicId }) => (
                  <li key={topicId}>
                    <a
                      href={`#${topicId}`}
                      aria-current={
                        topicId === topics.current ? "true" : undefined
                      }
                      onClick={jump(topicId)}
                    >
                      {topics.texts[topicId].title}
                    </a>
                  </li>
                ))}
              </ol>
            )}
          </li>
        );
      })}
    </Box>
  );
}
